// ft-sortable.ts — IN-PLACE sort + per-column filter for EXISTING server-rendered tables.
//
// The jQuery-free replacement for tablesorter on INTERACTIVE tables (dashboards, admin lists,
// AJAX search results) — the ones that can't become catalog-tables because their rows carry live
// handlers / ids / action buttons. Unlike render-table.ts (which rebuilds rows from a JSON payload),
// this NEVER rebuilds a cell: it reorders the existing <tr> nodes for sorting and toggles their
// display for filtering, so every row id, event handler, and live-update target survives untouched.
//
// Opt-in: a table gets enhanced by carrying class="ft-sortable" (NOT "tablesorter", so the legacy
// jQuery widget never touches it). The matching tablesorter theme class (-blue/-dark) is added at
// mount so the look matches the rest of the site. Per-column filter inputs (the "search bars") are
// injected by default; mark a <th> with class="ft-no-filter" to skip its box, or "ft-no-sort" to
// make a column unsortable (e.g. an action/settings column).
//
// Optional GLOBAL search box: put data-ft-sortable-search="<inputId>" on the <table> and the input
// with that id (typically in a page toolbar) filters across EVERY column's text. Space-separated
// tokens all-must-match, and it AND-combines with any per-column filter boxes.
//
// Re-scannable: window.ftSortableScan() mounts any not-yet-mounted .ft-sortable table. Call it again
// after an AJAX render swaps a table in (campaigns search, options/search/filter_* results, …).

function sortableTheme(): 'tablesorter-dark' | 'tablesorter-blue' {
    const m = document.cookie.match(/(?:^|;\s*)dark_mode=([^;]*)/);
    return m && decodeURIComponent(m[1] ?? '') === '1' ? 'tablesorter-dark' : 'tablesorter-blue';
}

interface CachedRow {
    tr: HTMLTableRowElement;
    keys: string[];   // per-column sort key: data-sort attr if present, else cell text
    texts: string[];  // per-column lowercased visible text, for filtering
}

function cellSortKey(td: HTMLTableCellElement): string {
    const explicit = td.getAttribute('data-sort');
    return (explicit !== null ? explicit : (td.textContent || '')).trim();
}

function mountSortable(table: HTMLTableElement): void {
    if (table.getAttribute('data-ft-sortable-mounted') === '1') return;
    table.setAttribute('data-ft-sortable-mounted', '1');
    table.classList.add(sortableTheme());

    const thead = table.tHead;
    const tbody = table.tBodies[0];
    if (!thead || thead.rows.length === 0 || !tbody) return;
    const mountedBody: HTMLTableSectionElement = tbody;

    const headerRow = thead.rows[0];
    if (!headerRow) return;
    const headers = Array.from(headerRow.cells) as HTMLTableCellElement[];

    // Cache the existing rows once. Sorting reorders these same nodes; filtering toggles their
    // display — neither rebuilds a cell, so row handlers/ids survive.
    const originalOrder: CachedRow[] = Array.from(tbody.rows).map((tr) => {
        const cells = Array.from(tr.cells) as HTMLTableCellElement[];
        return {
            tr,
            keys: cells.map(cellSortKey),
            texts: cells.map((c) => (c.textContent || '').trim().toLowerCase()),
        };
    });
    let order = originalOrder.slice();
    let sort: { i: number; dir: 'asc' | 'desc' } | null = null;

    // Optional global search box (filters across all columns), wired via data-ft-sortable-search.
    const globalSearchId = table.getAttribute('data-ft-sortable-search');
    const globalSearchInput = globalSearchId
        ? (document.getElementById(globalSearchId) as HTMLInputElement | null)
        : null;

    // ── Filter row (per-column "search bars"), injected unless opted out ──
    const filterInputs: Array<HTMLInputElement | null> = headers.map(() => null);
    const filterRow = document.createElement('tr');
    filterRow.className = 'tablesorter-filter-row';
    let anyFilter = false;
    headers.forEach((th, i) => {
        const td = document.createElement('td');
        const noFilter = th.classList.contains('ft-no-filter') || th.classList.contains('sorter-false');
        if (!noFilter) {
            const input = document.createElement('input');
            input.type = 'search';
            input.className = 'tablesorter-filter';
            input.autocomplete = 'off';
            input.setAttribute('aria-label', 'Filter column');
            input.addEventListener('input', applyFilter);
            filterInputs[i] = input;
            td.appendChild(input);
            anyFilter = true;
        }
        filterRow.appendChild(td);
    });
    if (anyFilter) thead.appendChild(filterRow);

    // ── Sortable headers ──
    headers.forEach((th, i) => {
        if (th.classList.contains('ft-no-sort')) {
            th.classList.add('sorter-false');
            return;
        }
        th.classList.add('tablesorter-header', 'tablesorter-headerUnSorted');
        th.setAttribute('role', 'button');
        th.setAttribute('tabindex', '0');
        const onSort = () => applySort(i);
        th.addEventListener('click', onSort);
        th.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(); }
        });
    });

    function applySort(i: number): void {
        const dir: 'asc' | 'desc' = sort && sort.i === i && sort.dir === 'asc' ? 'desc' : 'asc';
        sort = { i, dir };
        order = originalOrder
            .map((r, idx) => ({ r, idx }))
            .sort((a, b) => {
                const av = a.r.keys[i] ?? '';
                const bv = b.r.keys[i] ?? '';
                const an = parseFloat(av);
                const bn = parseFloat(bv);
                let cmp: number;
                if (av !== '' && bv !== '' && !Number.isNaN(an) && !Number.isNaN(bn)) {
                    cmp = an - bn;
                } else {
                    cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
                }
                return (dir === 'asc' ? cmp : -cmp) || (a.idx - b.idx); // stable
            })
            .map((x) => x.r);
        for (const r of order) mountedBody.appendChild(r.tr); // reorder existing nodes
        headers.forEach((th, idx) => {
            if (th.classList.contains('sorter-false')) return;
            th.classList.toggle('tablesorter-headerAsc', idx === i && dir === 'asc');
            th.classList.toggle('tablesorter-headerDesc', idx === i && dir === 'desc');
            th.classList.toggle('tablesorter-headerUnSorted', idx !== i);
            th.setAttribute('aria-sort', idx === i ? (dir === 'asc' ? 'ascending' : 'descending') : 'none');
        });
        applyFilter();
    }

    function applyFilter(): void {
        const active: Array<[number, string]> = [];
        filterInputs.forEach((inp, i) => {
            const v = inp ? inp.value.trim().toLowerCase() : '';
            if (v !== '') active.push([i, v]);
        });
        const globalTokens = globalSearchInput
            ? globalSearchInput.value.trim().toLowerCase().split(/\s+/).filter(Boolean)
            : [];
        let odd = true;
        for (const r of order) {
            let show = true;
            for (const [i, val] of active) {
                if (!(r.texts[i] || '').includes(val)) { show = false; break; }
            }
            if (show && globalTokens.length) {
                const hay = r.texts.join(' ');
                if (!globalTokens.every((t) => hay.includes(t))) show = false;
            }
            r.tr.style.display = show ? '' : 'none';
            if (show) {
                r.tr.classList.toggle('odd', odd);
                r.tr.classList.toggle('even', !odd);
                odd = !odd;
            }
        }
    }

    if (globalSearchInput) globalSearchInput.addEventListener('input', applyFilter);

    applyFilter(); // initial zebra
}

export function scanSortable(): void {
    document
        .querySelectorAll<HTMLTableElement>('table.ft-sortable:not([data-ft-sortable-mounted="1"])')
        .forEach(mountSortable);
}
